import { useApiHealthStore } from './health'
import { HttpError, fetchWithRetry, type FetchRetryOptions } from './http'

export interface BcbSgsPoint {
  /** ISO YYYY-MM-DD (converted from BCB's DD/MM/YYYY). */
  date: string
  /** Parsed floating point value of the series for that date. */
  value: number
}

export type BcbErrorCode =
  | 'NETWORK'
  | 'TIMEOUT'
  | 'HTTP_4XX'
  | 'HTTP_5XX'
  | 'RATE_LIMITED'
  | 'ABORTED'
  | 'PARSE'

export class BcbApiError extends Error {
  readonly code: BcbErrorCode
  override readonly cause: unknown

  constructor(code: BcbErrorCode, message: string, cause?: unknown) {
    super(message)
    this.name = 'BcbApiError'
    this.code = code
    this.cause = cause
  }
}

export const BCB_SGS_BASE_URL = 'https://api.bcb.gov.br/dados/serie/bcdata.sgs.10813/dados'

export interface BcbSgsClient {
  fetchSerie10813Range(startDateIso: string, endDateIso: string): Promise<BcbSgsPoint[]>
}

/**
 * Fetches series 10813 (USD compra, PTAX fechamento) within an inclusive
 * date range. Returns the published dates only — weekends and Brazilian
 * bank holidays are absent from the response by design (see D-02).
 */
export async function fetchSerie10813Range(
  startDateIso: string,
  endDateIso: string,
  options?: FetchRetryOptions,
): Promise<BcbSgsPoint[]> {
  const url = buildUrl(startDateIso, endDateIso)

  let response: Response
  try {
    response = await fetchWithRetry(url, options)
  } catch (err) {
    useApiHealthStore.getState().recordFailure('bcb')
    throw asBcbError(err)
  }

  let raw: unknown
  try {
    raw = await response.json()
  } catch (err) {
    useApiHealthStore.getState().recordFailure('bcb')
    throw new BcbApiError('PARSE', 'invalid JSON in BCB SGS response', err)
  }

  if (!Array.isArray(raw)) {
    useApiHealthStore.getState().recordFailure('bcb')
    throw new BcbApiError('PARSE', 'expected JSON array from BCB SGS')
  }

  useApiHealthStore.getState().recordSuccess('bcb')
  return raw.map(parsePoint).sort((a, b) => a.date.localeCompare(b.date))
}

function buildUrl(startDateIso: string, endDateIso: string): string {
  const start = isoToBrazilian(startDateIso)
  const end = isoToBrazilian(endDateIso)
  return `${BCB_SGS_BASE_URL}?formato=json&dataInicial=${start}&dataFinal=${end}`
}

function parsePoint(point: unknown, index: number): BcbSgsPoint {
  if (typeof point !== 'object' || point === null) {
    throw new BcbApiError('PARSE', `BCB point ${index} is not an object`)
  }
  const record = point as Record<string, unknown>
  const dataField = record['data']
  const valorField = record['valor']

  if (typeof dataField !== 'string') {
    throw new BcbApiError('PARSE', `BCB point ${index} missing string "data"`)
  }
  if (typeof valorField !== 'string') {
    throw new BcbApiError('PARSE', `BCB point ${index} missing string "valor"`)
  }

  const value = Number.parseFloat(valorField)
  if (!Number.isFinite(value)) {
    throw new BcbApiError('PARSE', `BCB point ${index} has invalid "valor"="${valorField}"`)
  }

  return { date: brazilianToIso(dataField), value }
}

function isoToBrazilian(iso: string): string {
  const parts = iso.split('-')
  if (parts.length !== 3) {
    throw new BcbApiError('PARSE', `invalid ISO date "${iso}"`)
  }
  const [year, month, day] = parts
  if (!year || !month || !day) {
    throw new BcbApiError('PARSE', `invalid ISO date "${iso}"`)
  }
  return `${day}/${month}/${year}`
}

function brazilianToIso(br: string): string {
  const parts = br.split('/')
  if (parts.length !== 3) {
    throw new BcbApiError('PARSE', `invalid BR date "${br}"`)
  }
  const [day, month, year] = parts
  if (!day || !month || !year) {
    throw new BcbApiError('PARSE', `invalid BR date "${br}"`)
  }
  return `${year}-${month}-${day}`
}

function asBcbError(err: unknown): BcbApiError {
  if (err instanceof HttpError) {
    return new BcbApiError(err.code, `BCB SGS request failed: ${err.message}`, err)
  }
  return new BcbApiError('NETWORK', err instanceof Error ? err.message : String(err), err)
}
